import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { TicketsApiService } from '../services/tickets-api.service';
import { TicketsConfigApiService } from '../services/tickets-config-api.service';
import { TicketsDrawerService } from '../services/tickets-drawer.service';
import { Ticket } from '../models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority,
  TicketRootCause,
  TicketStatus
} from '../models/tickets-config.model';
import { getOrigenLabel } from '../utils/ticket-format.util';
import { TicketsDetailTabsComponent } from '../tickets-detail-tabs/tickets-detail-tabs.component';
import {
  TicketActionType,
  TicketsActionsModalsComponent
} from '../tickets-actions-modals/tickets-actions-modals.component';

@Component({
  selector: 'app-tickets-detail',
  standalone: true,
  imports: [CommonModule, TicketsDetailTabsComponent, TicketsActionsModalsComponent],
  templateUrl: './tickets-detail.component.html'
})
export class TicketsDetailComponent implements OnInit, OnDestroy {
  @ViewChild(TicketsDetailTabsComponent) tabs?: TicketsDetailTabsComponent;

  ticket: Ticket | null = null;
  loading = false;
  error = '';

  /** Inc 73: ticket padre (sólo se carga cuando este ticket es un subticket). */
  parentTicket: Ticket | null = null;

  priorities: TicketPriority[] = [];
  statuses: TicketStatus[] = [];
  categories: TicketCategory[] = [];
  pipelines: TicketPipeline[] = [];
  stages: TicketPipelineStage[] = [];
  rootCauses: TicketRootCause[] = [];

  // Acción activa (controla el modal)
  activeAction: TicketActionType | null = null;

  /** Menú desplegable "Vínculos" de la barra superior. */
  linksMenuOpen = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private ticketsApi: TicketsApiService,
    private configApi: TicketsConfigApiService,
    private drawerService: TicketsDrawerService
  ) {}

  ngOnInit(): void {
    // Suscribirse a paramMap (no snapshot) para recargar al navegar entre
    // tickets sin cambiar de componente — p.ej. al crear un sub-ticket y saltar
    // al hijo (misma ruta /tickets/detalle/:id).
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = Number(params.get('id'));
      if (!id || id === this.ticket?.id) return;
      this.cargar(id);
    });

    // Recargar el ticket cuando se guarde desde el drawer
    this.drawerService.ticketSaved$.pipe(takeUntil(this.destroy$)).subscribe((saved) => {
      if (saved.id === this.ticket?.id) {
        this.ticket = saved;
        if (saved.idPipeline != null && saved.idPipeline !== this.ticket?.idPipeline) {
          this.cargarStages(saved.idPipeline);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    forkJoin({
      ticket: this.ticketsApi.getTicketById(id),
      priorities: this.configApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[]))),
      statuses: this.configApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[]))),
      categories: this.configApi.getCategories().pipe(catchError(() => of([] as TicketCategory[]))),
      pipelines: this.configApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      rootCauses: this.configApi.getRootCauses().pipe(catchError(() => of([] as TicketRootCause[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ ticket, priorities, statuses, categories, pipelines, rootCauses }) => {
          this.ticket = ticket;
          this.priorities = priorities;
          this.statuses = statuses;
          this.categories = categories;
          this.pipelines = pipelines;
          this.rootCauses = rootCauses;

          if (ticket.idPipeline != null) {
            this.cargarStages(ticket.idPipeline);
          }
          this.cargarPadre(ticket);
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al cargar el ticket';
          this.loading = false;
        }
      });
  }

  /**
   * Inc 73: si el ticket es un subticket, se carga el padre para poder mostrar su
   * número/asunto en "Asociaciones". Si la lectura falla se muestra el id.
   */
  private cargarPadre(ticket: Ticket): void {
    this.parentTicket = null;
    const parentId = ticket.idTicketParent;
    if (parentId == null) return;
    this.ticketsApi
      .getTicketById(parentId)
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe((p) => (this.parentTicket = p));
  }

  /** Inc 73: true cuando este ticket es hijo de otro (subticket / división). */
  get esSubTicket(): boolean {
    return this.ticket?.idTicketParent != null;
  }

  /** Etiqueta legible del ticket padre. */
  get parentLabel(): string {
    if (this.parentTicket) {
      return `${this.parentTicket.ticketNumber} — ${this.parentTicket.subject}`;
    }
    return `Ticket #${this.ticket?.idTicketParent}`;
  }

  /** Inc 73: abre el detalle del ticket padre (modal sobre la lista). */
  verTicketPadre(): void {
    const parentId = this.ticket?.idTicketParent;
    if (parentId == null) return;
    this.router.navigate(['/tickets'], { queryParams: { openTicket: parentId } });
  }

  /**
   * Inc 25: abre el DETALLE del lead de origen (no su edición). El detalle de un
   * lead es un modal sobre el kanban, por eso se navega a /leads?openLead=<id>.
   */
  verLead(idLead: number | null): void {
    if (idLead == null) return;
    this.router.navigate(['/leads'], { queryParams: { openLead: idLead } });
  }

  private cargarStages(pipelineId: number): void {
    this.configApi
      .getPipelineStages(pipelineId)
      .pipe(catchError(() => of([] as TicketPipelineStage[])))
      .subscribe((stages) => (this.stages = stages));
  }

  /**
   * Refresca el ticket desde el backend (usado tras Resolve/Reopen/Escalate
   * que devuelven DTOs sin TicketResponse completo).
   */
  private recargarTicket(): void {
    if (!this.ticket?.id) return;
    this.ticketsApi
      .getTicketById(this.ticket.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => (this.ticket = t));
  }

  // ===== Mappers para el sidebar =====
  origenLabel(source: string | null | undefined): string {
    return getOrigenLabel(source);
  }

  getPriorityLabel(idPriority: number | null): string {
    if (idPriority == null) return '-';
    return this.priorities.find((p) => p.id === idPriority)?.label || '-';
  }

  getStatusLabel(idStatus: number | null): string {
    if (idStatus == null) return '-';
    return this.statuses.find((s) => s.id === idStatus)?.label || '-';
  }

  getCategoryName(idCategory: number | null): string {
    if (idCategory == null) return '-';
    return this.categories.find((c) => c.id === idCategory)?.name || '-';
  }

  getPipelineName(idPipeline: number | null): string {
    if (idPipeline == null) return '-';
    return this.pipelines.find((p) => p.id === idPipeline)?.name || '-';
  }

  getStageName(idStage: number | null): string {
    if (idStage == null) return '-';
    return this.stages.find((s) => s.id === idStage)?.name || '-';
  }

  // ===== Visibilidad de acciones =====
  /**
   * Reopen sólo aplica si el status actual es resolved o terminal.
   */
  canReopen(): boolean {
    if (!this.ticket?.idTicketStatus) return false;
    const s = this.statuses.find((x) => x.id === this.ticket?.idTicketStatus);
    return !!(s && (s.isResolved || s.isTerminal));
  }

  /**
   * Resolve sólo si el status actual NO es resolved/terminal.
   */
  canResolve(): boolean {
    if (!this.ticket?.idTicketStatus) return true;
    const s = this.statuses.find((x) => x.id === this.ticket?.idTicketStatus);
    return !s || (!s.isResolved && !s.isTerminal);
  }

  // ===== Acciones =====
  volver(): void {
    this.router.navigate(['/tickets']);
  }

  editar(): void {
    if (this.ticket?.id) {
      this.drawerService.open({ ticketId: this.ticket.id });
    }
  }

  // ===== Menú "Vínculos" de la barra superior =====
  toggleLinksMenu(): void {
    this.linksMenuOpen = !this.linksMenuOpen;
  }

  closeLinksMenu(): void {
    this.linksMenuOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.linksMenuOpen) this.linksMenuOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.linksMenuOpen) this.linksMenuOpen = false;
  }

  abrirAccion(action: TicketActionType): void {
    this.linksMenuOpen = false;
    this.activeAction = action;
  }

  /**
   * Sub-ticket creado desde la pestaña "Vínculos" (componente de tabs).
   * Inc 74: NO se navega al hijo — el usuario se queda en el padre para poder ver
   * el evento recién registrado en su línea de tiempo. El refresco del historial y
   * de los vínculos lo hace el propio componente de tabs.
   */
  onSubTicketCreadoDesdeTabs(_child: Ticket): void {
    this.recargarTicket();
  }

  cerrarAccion(): void {
    this.activeAction = null;
  }

  onAccionCompletada(updated: Ticket | null): void {
    const action = this.activeAction;
    this.activeAction = null;

    if (updated && (action === 'reopen' || action === 'merge')) {
      // Reopen y merge devuelven el ticket actualizado.
      this.ticket = updated;
    } else if (updated && action === 'sub-ticket') {
      // Inc 74: quedarse en el ticket padre y refrescar historial + vínculos para
      // que el evento de creación del subticket sea visible sin recargar la página.
      this.tabs?.refrescarTrasSubTicket();
      this.recargarTicket();
    } else {
      // Resolve / Escalate / Split → recargar para tener el estado actualizado.
      this.recargarTicket();
    }
  }
}
